import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Button } from 'blaze-ui/Button'
import { stripePromise } from 'components/UpdatePaymentMethodModal/UpdatePaymentMethodModal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRef, useState } from 'react'
import { useRootStore } from 'stores/RootStore'
import Styles from './Checkout.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import { CardholderNameInput } from 'screens/LoginOrSignup/components/Blaze/StartFreeTrial/components/FreeTrialCardForm/FreeTrialCardForm'
import { observer } from 'mobx-react-lite'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Text } from 'blaze-ui/Text/Text'
import { BillingFrequency } from 'stores/PaywallStore'
import { Spinner } from 'common/Spinner/Spinner'
import { PlanHeader } from '../AllPlans/PlanHeader'
import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useQueryClient } from 'react-query'

import { useQuery } from 'react-query'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

export const useSetupIntent = (workspaceId: number | undefined) =>
  useQuery(
    ['setup-intent', workspaceId],
    () => WorkspaceClient.createSetupIntent(workspaceId as number).then((r) => r.data.client_secret),
    { enabled: !!workspaceId },
  )

const PaymentForm = observer(({ clientSecret }: { clientSecret: string }) => {
  const stripe = useStripe()
  const elements = useElements()
  const workspace = useCurrentWorkspace()
  const { paywallStore } = useRootStore()
  const { data: currentPlan } = useCurrentPlan()
  const queryClient = useQueryClient()

  const [ready, setReady] = useState(false)
  const [isCardHolderEmpty, setIsCardHolderEmpty] = useState(false)
  const [isCardPayment, setIsCardPayment] = useState(true)
  const cardHolderNameRef = useRef<HTMLInputElement>(null)

  const trialDaysLeft = currentPlan?.trial_days_left ?? -1

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements || !workspace) {
      return
    }

    // Validate cardholder name if using card payment
    if (isCardPayment && cardHolderNameRef.current?.value === '') {
      setIsCardHolderEmpty(true)
      return
    }
    // Process checkout using the store method
    const success = await paywallStore.processCheckout(
      stripe,
      elements,
      cardHolderNameRef.current?.value || '',
      workspace.id,
    )

    if (success) {
      queryClient.invalidateQueries([workspace.id, 'current-plan'])
      paywallStore.goToSuccess()
    }
  }

  const discount =
    trialDaysLeft > 0 &&
    paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL &&
    paywallStore.selectedPlanKey === PaymentPlanKey.BlazePro
      ? 0.08
      : 0.0

  const buttonLabel =
    paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL
      ? `Pay $${paywallStore.getMonthlyPrice(discount) * 12}`
      : `Pay $${paywallStore.getMonthlyPrice(discount)}`

  let monthlyPrice = paywallStore.getMonthlyPrice(discount)

  return (
    <form onSubmit={handleSubmit} className={Styles.form}>
      {/* Cardholder name input */}
      <div className={Styles.inputs}>
        <CardholderNameInput
          ref={cardHolderNameRef}
          isInvalid={isCardHolderEmpty}
          isDisabled={!ready || paywallStore.isProcessing}
          setIsInvalid={setIsCardHolderEmpty}
        />

        {/* Stripe Payment Element */}
        <PaymentElement
          onReady={() => setReady(true)}
          options={{
            paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'cashapp'],
            fields: {
              billingDetails: {
                name: 'never', // Disable Stripe's name field, we use our own
              },
            },
          }}
          onChange={(e) => {
            const selectedPaymentMethod = e?.value?.type
            setIsCardPayment(selectedPaymentMethod === 'card')
            if (paywallStore.errorMessage) paywallStore.setError(null)
          }}
        />
      </div>

      {paywallStore.errorMessage && <div className={Styles.errorMessage}>{paywallStore.errorMessage}</div>}

      {ready && (
        <p className={Styles.authorizationMsg}>
          By continuing, you agree to Blaze's{' '}
          <a target="_blank" href="https://www.blaze.ai/terms" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a target="_blank" href="https://www.blaze.ai/privacy-policy" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      )}

      <div className={Styles.footer}>
        <ResponsiveDesign.Mobile>
          <Button
            size="xl"
            variant="primary"
            type="submit"
            endIcon={ArrowRight}
            fullWidth
            isDisabled={!ready || paywallStore.isProcessing}
          >
            {paywallStore.isProcessing ? 'Processing...' : 'Subscribe to Blaze'}
          </Button>
        </ResponsiveDesign.Mobile>
        <ResponsiveDesign.Desktop>
          <div className={Styles.buttonContainer}>
            <Button variant="tertiary" onPress={paywallStore.goToPlanSelect} size="lg">
              Back
            </Button>
            <div className={Styles.buttonContainerRight}>
              <Text variant="placeholder" color="var(--dark-80)">
                ${monthlyPrice} per month {paywallStore.selectedBillingFrequency === 'annual' ? 'for 12 months' : ''}
              </Text>
              <Button variant="primary" size="lg" type="submit" isDisabled={!ready || paywallStore.isProcessing}>
                {paywallStore.isProcessing ? 'Processing...' : buttonLabel}
              </Button>
            </div>
          </div>
        </ResponsiveDesign.Desktop>
      </div>
    </form>
  )
})

// Main component that provides the Elements context
export const CheckoutScreen = observer(() => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { data: clientSecret, isLoading } = useSetupIntent(currentWorkspaceId)
  const { data: currentPlan } = useCurrentPlan()
  const { paywallStore } = useRootStore()
  if (isLoading || !clientSecret) {
    return (
      <div className={Styles.container}>
        <div className={Styles.header}>
          <ResponsiveDesign.Mobile>
            <Button variant="ghost" color="var(--dark-80)" onPress={paywallStore.goToPreviousScreen}>
              Back
            </Button>
          </ResponsiveDesign.Mobile>
          <ResponsiveDesign.Desktop>
            <IconButton variant="tertiary" onPress={paywallStore.goToPlanSelect} icon={ArrowLeft} size="xs" />
          </ResponsiveDesign.Desktop>
          <ResponsiveDesign.Desktop>
            <Heading level={3}>Let’s become a Pro. Subscribe to Blaze!</Heading>
          </ResponsiveDesign.Desktop>
        </div>
        <div className={Styles.loader}>
          <Spinner />
        </div>
      </div>
    )
  }

  const trialDaysLeft = currentPlan?.trial_days_left ?? -1

  return (
    <div className={Styles.container}>
      <div className={Styles.header}>
        <ResponsiveDesign.Mobile>
          <Button variant="ghost" color="var(--dark-80)" onPress={paywallStore.goToPreviousScreen}>
            Back
          </Button>
        </ResponsiveDesign.Mobile>
        <ResponsiveDesign.Desktop>
          <IconButton variant="tertiary" onPress={paywallStore.goToPreviousScreen} icon={ArrowLeft} size="xs" />
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Desktop>
          <Heading level={3}>Let’s become a Pro. Subscribe to Blaze!</Heading>
        </ResponsiveDesign.Desktop>
      </div>
      <div className={Styles.content}>
        <PlanHeader
          discount={trialDaysLeft > 0}
          plan={paywallStore.getSelectedPlan() as PaymentPlan}
          frequency={paywallStore.selectedBillingFrequency}
          compact
        />
        <ResponsiveDesign.Mobile>
          <Heading level={1}>Upgrade today to keep generating content that helps you grow in Blaze!</Heading>
        </ResponsiveDesign.Mobile>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#6159FF',
                colorBackground: '#ffffff',
                colorText: '#1A1523',
              },
            },
          }}
        >
          <PaymentForm clientSecret={clientSecret} />
        </Elements>
      </div>
    </div>
  )
})
